import type { Action } from '@/types/workflowDefinition';
import type { GraphEdge, GraphNode } from '@/hooks/useWorkflowGraph';
import { isForEachFull } from '@/hooks/useForEachConstraint';
import { getNodeOutputHandles } from '../workflowHelpers';

export type TerminalHandle = {
  nodeId: string;
  handle: string;
  nodeType: string | undefined;
  actionName: string;
  scopeParentId?: string | undefined;
  branchType?: string | undefined;
};

export type WorkflowGraphIndexes = {
  terminalHandles: TerminalHandle[];
  terminalNodeIds: ReadonlySet<string>;
  terminalHandlesByNodeId: ReadonlyMap<string, readonly TerminalHandle[]>;
  scopeContainerIdsByChildId: ReadonlyMap<string, ReadonlySet<string>>;
};

const MULTI_HANDLE_TYPES = new Set(['if', 'switch', 'foreach']);

export const buildWorkflowGraphIndexes = (
  graphNodes: readonly GraphNode[],
  graphEdges: readonly GraphEdge[],
  workflowActions: Record<string, Action> | undefined,
): WorkflowGraphIndexes => {
  const connectedHandles = new Set<string>();
  const outgoingSources = new Set<string>();

  for (const edge of graphEdges) {
    const handle = edge.sourceHandle ?? 'output';
    connectedHandles.add(`${edge.source}::${handle}`);
    outgoingSources.add(edge.source);
  }

  const terminalHandles: TerminalHandle[] = [];
  const terminalHandlesByNodeId = new Map<string, TerminalHandle[]>();
  const scopeContainerIdsByChildId = new Map<string, Set<string>>();

  for (const node of graphNodes) {
    const nodeType = node.type ?? '';
    if (nodeType === 'scopeContainer') {
      for (const childId of node.data.childNodeIds ?? []) {
        let containerIds = scopeContainerIdsByChildId.get(childId);
        if (!containerIds) {
          containerIds = new Set();
          scopeContainerIdsByChildId.set(childId, containerIds);
        }
        containerIds.add(node.id);
      }
      continue;
    }

    const actionName =
      node.data.actionName ?? node.id.replace(/^(trigger-|action-)/, '');
    const scopeParentId = node.data.scopeParentId;
    const branchType = node.data.branchType;
    if (scopeParentId && branchType === 'loop') {
      const scopeParentActionName = scopeParentId.replace(/^action-/, '');
      if (isForEachFull(workflowActions, scopeParentActionName)) continue;
    }

    const handles = MULTI_HANDLE_TYPES.has(nodeType)
      ? getNodeOutputHandles(nodeType, node.data.cases)
      : outgoingSources.has(node.id)
        ? []
        : ['output'];

    for (const handle of handles) {
      if (connectedHandles.has(`${node.id}::${handle}`)) continue;
      const terminalHandle: TerminalHandle = {
        nodeId: node.id,
        handle,
        nodeType,
        actionName,
        scopeParentId,
        branchType,
      };
      terminalHandles.push(terminalHandle);
      const grouped = terminalHandlesByNodeId.get(node.id);
      if (grouped) {
        grouped.push(terminalHandle);
      } else {
        terminalHandlesByNodeId.set(node.id, [terminalHandle]);
      }
    }
  }

  return {
    terminalHandles,
    terminalNodeIds: new Set(terminalHandlesByNodeId.keys()),
    terminalHandlesByNodeId,
    scopeContainerIdsByChildId,
  };
};
